'use client';

import { FormEvent, useEffect, useState } from 'react';
import { useLocale } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Check, Loader2, Upload, Info, Search, Star, Sparkles, PenTool } from 'lucide-react';
import { ARTWORK_ACCEPT_ATTRIBUTE } from '@/lib/uploads/artwork-formats';

type Locale = 'nl' | 'de' | 'en' | 'fr';

const popularProducts = [
  { slug: 'posters', nl: 'Posters', de: 'Poster', en: 'Posters', fr: 'Affiches' },
  { slug: 'canvas-print', nl: 'Canvas', de: 'Leinwand', en: 'Canvas', fr: 'Toile' },
  { slug: 'spandoek', nl: 'Spandoek', de: 'Banner', en: 'Banner', fr: 'Banderole' },
  { slug: 'visitekaartjes', nl: 'Visitekaartjes', de: 'Visitenkarten', en: 'Business cards', fr: 'Cartes de visite' },
  { slug: 'flyers', nl: 'Flyers', de: 'Flyer', en: 'Flyers', fr: 'Prospectus' },
  { slug: 'banners', nl: 'Banners', de: 'Banner', en: 'Banners', fr: 'Bannieres' },
  { slug: 'raamsticker', nl: 'Raamsticker', de: 'Fensteraufkleber', en: 'Window sticker', fr: 'Sticker fenetre' },
  { slug: 'gevelreclame', nl: 'Gevelreclame', de: 'Fassadenwerbung', en: 'Facade sign', fr: 'Enseigne facade' },
  { slug: 'vlaggen', nl: 'Vlaggen', de: 'Fahnen', en: 'Flags', fr: 'Drapeaux' },
  { slug: 'beursdisplay', nl: 'Beursdisplay', de: 'Messestand', en: 'Exhibition stand', fr: 'Stand exposition' },
  { slug: 'briefpapier', nl: 'Briefpapier', de: 'Briefpapier', en: 'Letterhead', fr: 'Papier a lettres' },
  { slug: 'sticker', nl: 'Stickers', de: 'Aufkleber', en: 'Stickers', fr: 'Autocollants' },
];

const examples = [
  { title: 'Restaurant menu', desc: 'Dubbelzijdig A4 menu met logo, kleurenschema passend bij branding.' },
  { title: 'Verjaardagscadeau', desc: 'Foto op canvas 60x40cm met persoonlijke tekst in sierlijk lettertype.' },
  { title: 'Bedrijfsbanner', desc: 'Spandoek 300x100cm met bedrijfslogo, slogan en contactgegevens.' },
  { title: 'Uitnodiging', desc: 'Dubbelzijdige flyer A5 voor bruiloft met foto\'s en gouden details.' },
];

const copy: Record<Locale, {
  title: string; subtitle: string; intro: string;
  howItWorks: string[]; howTitle: string;
  searchPlaceholder: string; popular: string; orType: string;
  name: string; email: string; phone: string;
  product: string; productPlaceholder: string;
  width: string; height: string;
  file: string; fileHint: string;
  description: string; descriptionPlaceholder: string;
  example: string; exampleText: string;
  quality: string; qualityText: string;
  free: string; freeText: string;
  submit: string; submitting: string;
  success: string; successText: string;
  error: string;
  examplesTitle: string; examplesText: string;
  seoTitle: string; seoText: string;
  detailsTitle: string; loginHint: string; googleLogin: string; loggedIn: string; productTitle: string; designTitle: string; searching: string;
}> = {
  nl: {
    title: 'Gratis ontwerpservice', subtitle: 'Laat een ontwerp maken door onze designers. Helemaal gratis.',
    intro: 'Niet handig met ontwerpen? Geen probleem. Onze designers maken een printklaar ontwerp voor je — volledig gratis. Of je nu een spandoek, poster, flyer of visitekaartjes nodig hebt: wij zorgen dat het er professioneel uitziet.',
    howTitle: 'Zo werkt het',
    howItWorks: ['Vul het formulier in en beschrijf wat je wilt', 'Upload foto\'s of voorbeelden ter inspiratie', 'Onze designer maakt een ontwerp op maat', 'Je ontvangt het ontwerp per e-mail binnen 1-2 werkdagen', 'Tevreden? Bestel direct je product met het ontwerp'],
    searchPlaceholder: 'Zoek je product...', popular: 'Populair:', orType: 'of typ zelf',
    name: 'Naam', email: 'E-mail', phone: 'Telefoon',
    product: 'Product', productPlaceholder: 'Bijv: Foto op canvas, Spandoek, Visitekaartjes...',
    width: 'Breedte (cm)', height: 'Hoogte (cm)',
    file: 'Bestand uploaden', fileHint: 'Foto of afbeelding die in het ontwerp moet (max 20MB)',
    description: 'Beschrijving van ontwerp', descriptionPlaceholder: 'Beschrijf zo duidelijk mogelijk wat je wilt. Hoe meer details, hoe beter het ontwerp.',
    example: 'Voorbeeld:', exampleText: '"Ik wil de geüploade foto graag in zwart/wit met daarop in het groot de tekst \'Familie\' en een klein hartje. Het lettertype wil ik graag in een speelse stijl. Als achtergrond een zachte pastelkleur."',
    quality: 'Foto kwaliteit', qualityText: 'Gebruik foto\'s van de hoogst mogelijke kwaliteit. Gratis stockfoto\'s vind je op Pexels.com en Pixabay.com.',
    free: 'Gratis service', freeText: 'Onze ontwerpservice is 100% gratis. Om dit zo te houden vragen we je om zo duidelijk mogelijk te beschrijven wat je wilt. Wijzigingen achteraf kunnen extra kosten met zich meebrengen.',
    submit: 'Gratis ontwerp aanvragen', submitting: 'Aanvragen...',
    success: 'Aanvraag verzonden!', successText: 'Je ontwerpaanvraag is ontvangen. We sturen het ontwerp binnen 1-2 werkdagen naar je e-mail.',
    error: 'Aanvraag mislukt. Probeer opnieuw of mail info@printen.eu.',
    examplesTitle: 'Voorbeelden van ontwerpen', examplesText: 'Onze designers hebben onder andere deze ontwerpen gemaakt voor klanten:',
    seoTitle: 'Professionele ontwerpen voor al je printproducten',
    seoText: 'Bij Printen.eu begrijpen we dat niet iedereen een geboren designer is. Daarom bieden we een volledig gratis ontwerpservice aan. Onze ervaren designers maken ontwerpen voor al onze producten: van posters en canvas tot spandoeken, banners, flyers, visitekaartjes, raamstickers, gevelreclame, vlaggen, beursdisplays en nog veel meer. Stuur ons je foto\'s, logo\'s of een ruwe schets, en wij maken er een professioneel, printklaar ontwerp van.\n\nOnze ontwerpservice is geschikt voor zowel particulieren als bedrijven. Of je nu een eenmalig cadeau wilt laten ontwerpen of regelmatig nieuw drukwerk nodig hebt voor je onderneming — wij staan voor je klaar. Het ontwerp wordt afgestemd op de technische specificaties van het door jou gekozen product, zodat je zeker weet dat het eindresultaat perfect is.\n\nTwijfel je over het formaat, materiaal of de afwerking? Onze designers denken met je mee en geven advies over de beste keuzes voor jouw toepassing.',
    detailsTitle: 'Jouw gegevens', loginHint: 'Inloggen voor sneller aanvragen en ontwerpen terugzien.', googleLogin: 'Google login', loggedIn: 'Ingelogd als', productTitle: 'Product', designTitle: 'Jouw ontwerp', searching: 'Zoeken...',
  },
  de: {
    title: 'Kostenloser Design-Service', subtitle: 'Lassen Sie ein Design von unseren Designern erstellen. Vollig kostenlos.',
    intro: 'Nicht gut im Design? Kein Problem. Unsere Designer erstellen ein druckfertiges Design fur Sie — komplett kostenlos.',
    howTitle: 'So funktioniert es',
    howItWorks: ['Fullen Sie das Formular aus und beschreiben Sie, was Sie mochten', 'Laden Sie Fotos oder Vorlagen zur Inspiration hoch', 'Unser Designer erstellt ein individuelles Design', 'Sie erhalten das Design per E-Mail innerhalb von 1-2 Werktagen', 'Zufrieden? Bestellen Sie direkt Ihr Produkt mit dem Design'],
    searchPlaceholder: 'Produkt suchen...', popular: 'Beliebt:', orType: 'oder selbst eintippen',
    name: 'Name', email: 'E-Mail', phone: 'Telefon',
    product: 'Produkt', productPlaceholder: 'Z.B.: Foto auf Leinwand, Banner, Visitenkarten...',
    width: 'Breite (cm)', height: 'Hohe (cm)',
    file: 'Datei hochladen', fileHint: 'Foto oder Bild fur das Design (max. 20MB)',
    description: 'Design-Beschreibung', descriptionPlaceholder: 'Beschreiben Sie so genau wie moglich, was Sie mochten.',
    example: 'Beispiel:', exampleText: '"Ich mochte das hochgeladene Foto in schwarz/weiss mit dem Text \'Familie\' und einem kleinen Herz in einer verspielten Schriftart."',
    quality: 'Fotoqualitat', qualityText: 'Verwenden Sie Fotos in hochster Qualitat. Kostenlose Stockfotos finden Sie auf Pexels.com und Pixabay.com.',
    free: 'Kostenloser Service', freeText: 'Unser Design-Service ist 100% kostenlos. Bitte beschreiben Sie so genau wie moglich, was Sie wunschen.',
    submit: 'Kostenloses Design anfordern', submitting: 'Wird gesendet...',
    success: 'Anfrage gesendet!', successText: 'Ihre Design-Anfrage ist eingegangen. Wir senden das Design innerhalb von 1-2 Werktagen per E-Mail.',
    error: 'Anfrage fehlgeschlagen.',
    examplesTitle: 'Design-Beispiele', examplesText: 'Unsere Designer haben unter anderem diese Designs fur Kunden erstellt:',
    seoTitle: 'Professionelle Designs fur alle Ihre Druckprodukte',
    seoText: 'Bei Printen.eu verstehen wir, dass nicht jeder ein Designer ist. Deshalb bieten wir einen komplett kostenlosen Design-Service an.',
    detailsTitle: 'Ihre Daten', loginHint: 'Einloggen für schnellere Anfragen und gespeicherte Designs.', googleLogin: 'Google Login', loggedIn: 'Eingeloggt als', productTitle: 'Produkt', designTitle: 'Ihr Design', searching: 'Suchen...',
  },
  en: {
    title: 'Free Design Service', subtitle: 'Let our designers create a design for you. Completely free.',
    intro: 'Not skilled at designing? No problem. Our designers create a print-ready design for you — completely free. Whether you need a banner, poster, flyer or business cards: we make sure it looks professional.',
    howTitle: 'How it works',
    howItWorks: ['Fill in the form and describe what you want', 'Upload photos or examples for inspiration', 'Our designer creates a custom design', 'Receive the design by email within 1-2 business days', 'Happy? Order your product directly with the design'],
    searchPlaceholder: 'Search product...', popular: 'Popular:', orType: 'or type yourself',
    name: 'Name', email: 'Email', phone: 'Phone',
    product: 'Product', productPlaceholder: 'E.g.: Canvas print, Banner, Business cards...',
    width: 'Width (cm)', height: 'Height (cm)',
    file: 'Upload file', fileHint: 'Photo or image for the design (max 20MB)',
    description: 'Design description', descriptionPlaceholder: 'Describe as clearly as possible what you want. More details = better design.',
    example: 'Example:', exampleText: '"I want the uploaded photo in black and white with the text \'Family\' and a small heart in a playful font. Soft pastel background."',
    quality: 'Photo quality', qualityText: 'Use the highest quality photos. Free stock photos at Pexels.com and Pixabay.com.',
    free: 'Free service', freeText: 'Our design service is 100% free. Please describe as clearly as possible what you want.',
    submit: 'Request free design', submitting: 'Submitting...',
    success: 'Request sent!', successText: 'Your design request has been received. We will email the design within 1-2 business days.',
    error: 'Request failed. Please try again.',
    examplesTitle: 'Design examples', examplesText: 'Our designers have created these designs for customers:',
    seoTitle: 'Professional designs for all your print products',
    seoText: 'At Printen.eu we understand that not everyone is a designer. That\'s why we offer a completely free design service.',
    detailsTitle: 'Your details', loginHint: 'Sign in for faster requests and to view saved designs.', googleLogin: 'Google login', loggedIn: 'Signed in as', productTitle: 'Product', designTitle: 'Your design', searching: 'Searching...',
  },
  fr: {
    title: 'Service design gratuit', subtitle: 'Laissez nos designers créer un design pour vous. Entierement gratuit.',
    intro: 'Pas doue en design ? Nos designers creent un design prêt à imprimer pour vous — totalement gratuit.',
    howTitle: 'Comment ca marche',
    howItWorks: ['Remplissez le formulaire et decrivez ce que vous voulez', 'Telechargez des photos ou exemples', 'Notre designer cree un design sur mesure', 'Recevez le design par e-mail sous 1-2 jours ouvres', 'Satisfait ? Commandez directement avec le design'],
    searchPlaceholder: 'Rechercher un produit...', popular: 'Populaire:', orType: 'ou tapez vous-meme',
    name: 'Nom', email: 'E-mail', phone: 'Telephone',
    product: 'Produit', productPlaceholder: 'Ex.: Photo sur toile, Banderole, Cartes de visite...',
    width: 'Largeur (cm)', height: 'Hauteur (cm)',
    file: 'Telecharger fichier', fileHint: 'Photo ou image pour le design (max 20MB)',
    description: 'Description du design', descriptionPlaceholder: 'Decrivez aussi clairement que possible ce que vous voulez.',
    example: 'Exemple:', exampleText: '"Je veux la photo en noir et blanc avec le texte \'Famille\' et un petit coeur dans une police ludique. Fond pastel doux."',
    quality: 'Qualite photo', qualityText: 'Photos de la plus haute qualite. Photos gratuites sur Pexels.com et Pixabay.com.',
    free: 'Service gratuit', freeText: 'Notre service design est 100% gratuit. Decrivez aussi clairement que possible.',
    submit: 'Demander un design gratuit', submitting: 'Envoi...',
    success: 'Demande envoyee !', successText: 'Votre demande a bien ete recue. Design par e-mail sous 1-2 jours.',
    error: 'Demande echouee.',
    examplesTitle: 'Exemples de designs', examplesText: 'Nos designers ont cree ces designs pour des clients :',
    seoTitle: 'Designs professionnels pour tous vos produits',
    seoText: 'Chez Printen.eu, nous comprenons que tout le monde n\'est pas designer. C\'est pourquoi nous offrons un service de design gratuit.',
    detailsTitle: 'Vos informations', loginHint: 'Connectez-vous pour demander plus vite et retrouver vos designs.', googleLogin: 'Connexion Google', loggedIn: 'Connecté en tant que', productTitle: 'Produit', designTitle: 'Votre design', searching: 'Recherche...',
  },
};

export default function OntwerpServicePage() {
  const locale = useLocale();
  const safeLocale: Locale = locale in copy ? (locale as Locale) : 'en';
  const t = copy[safeLocale];
  const [status, setStatus] = useState<'form' | 'loading' | 'success'>('form');
  const [user, setUser] = useState<{name:string;email:string}|null>(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', productName: '', width: '', height: '', description: '' });

  // Check if user is logged in via Google
  useEffect(() => {
    fetch('/api/account/me').then(r => r.json()).then(d => {
      if (d.user) {
        setUser({ name: [d.user.firstName, d.user.lastName].filter(Boolean).join(' ') || '', email: d.user.email || '' });
        setForm(f => ({ ...f, name: f.name || [d.user.firstName, d.user.lastName].filter(Boolean).join(' ') || '', email: f.email || d.user.email || '' }));
      }
    }).catch(() => {});
  }, []);

  function loginWithGoogle() {
    window.location.href = `/api/account/google/start?redirect=/${locale}/ontwerp-service`;
  }
  const [file, setFile] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<{name:string;slug:string;category?:string}[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const update = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));

  // Live product search via API
  useEffect(() => {
    if (!search || search.length < 2) { setSearchResults([]); return; }
    const timeout = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await fetch(`/api/products/search?q=${encodeURIComponent(search)}`);
        const data = await res.json();
        if (data.success) setSearchResults(data.products || []);
      } catch { setSearchResults([]); }
      setSearchLoading(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.name || !form.email || !form.productName) return;
    setStatus('loading');
    setErrorMessage(null);
    try {
      const fd = new FormData();
      fd.set('name', form.name); fd.set('email', form.email); fd.set('phone', form.phone);
      fd.set('productName', form.productName); fd.set('width', form.width); fd.set('height', form.height);
      fd.set('description', form.description); fd.set('locale', safeLocale);
      if (file) fd.set('file', file);
      const res = await fetch('/api/design-request', { method: 'POST', body: fd });
      if (res.ok) { setStatus('success'); return; }
      // A body over the platform limit is refused before our route runs and
      // comes back without JSON; fall back to the generic message then.
      const data = await res.json().catch(() => null);
      setErrorMessage(typeof data?.error === 'string' ? data.error : t.error);
      setStatus('form');
    } catch { setErrorMessage(t.error); setStatus('form'); }
  }

  return (
    <div className="container mx-auto max-w-5xl px-4 py-12">
      {/* Hero */}
      <div className="text-center mb-12">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-green-400 to-emerald-600 mb-6 shadow-lg">
          <PenTool className="h-8 w-8 text-white" />
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-3">{t.title}</h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">{t.subtitle}</p>
      </div>

      {/* How it works */}
      <div className="grid grid-cols-5 gap-4 mb-12">
        {t.howItWorks.map((step, i) => (
          <div key={i} className="text-center">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-700 font-bold text-sm mb-2">{i + 1}</div>
            <p className="text-xs text-gray-600 leading-relaxed">{step}</p>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          {status === 'success' ? (
            <Card className="p-12 text-center">
              <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-green-100 mb-6"><Check className="h-10 w-10 text-green-600" /></div>
              <h2 className="text-2xl font-bold mb-3">{t.success}</h2>
              <p className="text-gray-600 max-w-md mx-auto">{t.successText}</p>
            </Card>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <Card className="p-6">
                <h2 className="font-bold text-lg mb-4">{t.detailsTitle}</h2>
                {!user && (
                  <div className="mb-4 rounded-lg bg-blue-50 border border-blue-200 p-3 flex items-center justify-between">
                    <p className="text-sm text-blue-800">{t.loginHint}</p>
                    <Button type="button" variant="outline" size="sm" onClick={loginWithGoogle} className="flex-none">{t.googleLogin}</Button>
                  </div>
                )}
                {user && (
                  <p className="text-xs text-green-700 mb-3">✓ {t.loggedIn} {user.email}</p>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="text-sm font-medium mb-1 block">{t.name} *</label><Input value={form.name} onChange={e => update('name', e.target.value)} required /></div>
                  <div><label className="text-sm font-medium mb-1 block">{t.email} *</label><Input type="email" value={form.email} onChange={e => update('email', e.target.value)} required /></div>
                  <div className="col-span-2"><label className="text-sm font-medium mb-1 block">{t.phone}</label><Input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} /></div>
                </div>
              </Card>

              <Card className="p-6">
                <h2 className="font-bold text-lg mb-4">{t.productTitle}</h2>
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t.searchPlaceholder} className="pl-10" />
                  {(searchResults.length > 0 || searchLoading) && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {searchLoading && <div className="px-4 py-2 text-sm text-gray-400">{t.searching}</div>}
                      {searchResults.map(p => (
                        <button key={p.slug} type="button" className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2" onClick={() => { update('productName', p.name); setSearch(''); setSearchResults([]); }}>
                          <Star className="h-3 w-3 text-amber-400 flex-none" />{p.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="text-xs text-gray-500">{t.popular}</span>
                  {popularProducts.slice(0, 8).map(p => (
                    <Badge key={p.slug} variant="secondary" className="cursor-pointer hover:bg-gray-200 text-xs" onClick={() => update('productName', (p as any)[safeLocale] || p.nl)}>{(p as any)[safeLocale] || p.nl}</Badge>
                  ))}
                </div>
                <label className="text-sm font-medium mb-1 block">{t.product} *</label>
                <Input value={form.productName} onChange={e => update('productName', e.target.value)} placeholder={t.orType + '...'} required />
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div><label className="text-sm font-medium mb-1 block">{t.width}</label><Input value={form.width} onChange={e => update('width', e.target.value)} placeholder="100" /></div>
                  <div><label className="text-sm font-medium mb-1 block">{t.height}</label><Input value={form.height} onChange={e => update('height', e.target.value)} placeholder="50" /></div>
                </div>
              </Card>

              <Card className="p-6">
                <h2 className="font-bold text-lg mb-4">{t.designTitle}</h2>
                <div>
                  <label className="text-sm font-medium mb-1 block">{t.file}</label>
                  <Input type="file" accept={`${ARTWORK_ACCEPT_ATTRIBUTE},.psd`} onChange={e => setFile(e.target.files?.[0] || null)} />
                  <p className="text-xs text-gray-500 mt-1">{t.fileHint}</p>
                </div>
                <div className="mt-4">
                  <label className="text-sm font-medium mb-1 block">{t.description}</label>
                  <Textarea value={form.description} onChange={e => update('description', e.target.value)} placeholder={t.descriptionPlaceholder} rows={5} />
                  <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                    <Info className="h-4 w-4 text-blue-600 flex-none mt-0.5" />
                    <div><p className="text-xs font-medium text-blue-800">{t.example}</p><p className="text-xs text-blue-700 mt-0.5">{t.exampleText}</p></div>
                  </div>
                </div>
              </Card>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-2">
                  <Upload className="h-5 w-5 text-amber-600 flex-none mt-0.5" />
                  <div><p className="text-sm font-medium text-amber-800">{t.quality}</p><p className="text-xs text-amber-700">{t.qualityText}</p></div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-2">
                  <Sparkles className="h-5 w-5 text-green-600 flex-none mt-0.5" />
                  <div><p className="text-sm font-medium text-green-800">{t.free}</p><p className="text-xs text-green-700">{t.freeText}</p></div>
                </div>
              </div>

              {errorMessage && (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{errorMessage}</p>
              )}

              <Button type="submit" className="w-full" size="lg" disabled={status === 'loading'}>
                {status === 'loading' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PenTool className="h-4 w-4 mr-2" />}
                {status === 'loading' ? t.submitting : t.submit}
              </Button>
            </form>
          )}

          {/* SEO text */}
          <div className="mt-16 border-t pt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t.seoTitle}</h2>
            {t.seoText.split('\n\n').map((p, i) => <p key={i} className="text-gray-600 mb-4 leading-relaxed">{p}</p>)}
          </div>
        </div>

        {/* Sidebar — examples */}
        <div className="space-y-6">
          <Card className="p-6 sticky top-24">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2"><Star className="h-5 w-5 text-amber-500" />{t.examplesTitle}</h3>
            <p className="text-sm text-gray-500 mb-4">{t.examplesText}</p>
            <div className="space-y-4">
              {examples.map((ex, i) => (
                <div key={i} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                  <p className="text-sm font-semibold text-gray-800">{ex.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{ex.desc}</p>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
